import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import {
  buildAuthUserVersionInvalidation,
  publishAuthSessionInvalidation,
} from '../utils/auth';

const API_URL = getApiUrl();

const RedefinirSenha = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!token) {
      setError('Link inválido ou expirado. Solicite uma nova recuperação de senha.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('A confirmação precisa ser igual à nova senha.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao redefinir senha.');
      }

      publishAuthSessionInvalidation(
        window,
        buildAuthUserVersionInvalidation(data.sessionInvalidation, 'password_reset')
      );
      window.location.replace('/login?reason=password_reset');
    } catch (err) {
      setError(err.message || 'Falha ao redefinir senha.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        background: 'linear-gradient(135deg, #111827 0%, #1f2937 48%, #f97316 100%)',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 520, borderRadius: 4, boxShadow: '0 28px 70px rgba(15, 23, 42, 0.32)' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  bgcolor: '#111827',
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <LockResetRoundedIcon />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827' }}>
                Criar nova senha
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Defina uma nova senha para voltar a acessar o Brutos Delivery.
              </Typography>
            </Stack>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {!token ? (
                  <Alert severity="warning">
                    Link inválido ou expirado. Solicite uma nova recuperação de senha.
                  </Alert>
                ) : null}
                {error ? <Alert severity="error">{error}</Alert> : null}
                <TextField
                  label="Nova senha"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  type={showPasswords ? 'text' : 'password'}
                  autoComplete="new-password"
                  helperText="Use pelo menos 8 caracteres combinando letras e números."
                  fullWidth
                  required
                  disabled={!token}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPasswords((prev) => !prev)} edge="end">
                          {showPasswords ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Confirmar nova senha"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  type={showPasswords ? 'text' : 'password'}
                  autoComplete="new-password"
                  fullWidth
                  required
                  disabled={!token}
                />
                <Button type="submit" size="large" variant="contained" disabled={submitting || !token}>
                  {submitting ? 'Salvando...' : 'Salvar nova senha'}
                </Button>
                <Button component={RouterLink} to="/login" variant="text" size="small">
                  Voltar para login
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RedefinirSenha;
